import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Bell,
  Calendar,
  Clock,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Trophy,
  ShieldCheck,
  Tag,
} from "lucide-react";
import { AppNotification, markNotificationAsRead } from "../../services/notificationService";

interface NotificationDetailModalProps {
  notification: AppNotification | null;
  onClose: () => void;
  onNavigate?: (path: string) => void;
  cleanUid?: string;
}

export const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  onClose,
  onNavigate,
  cleanUid,
}) => {
  // Bloqueia scroll do body da página enquanto o modal de detalhe estiver aberto
  useEffect(() => {
    if (!notification) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [notification]);

  if (!notification) return null;

  const handleMarkRead = async () => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id, cleanUid || notification.userId);
    }
  };

  const handleActionClick = async () => {
    await handleMarkRead();
    if (notification.linkUrl) {
      onClose();
      if (onNavigate) {
        onNavigate(notification.linkUrl);
      } else if (typeof window !== "undefined") {
        window.location.href = notification.linkUrl;
      }
    }
  };

  const formatDateTime = (isoString?: string) => {
    if (!isoString) return { date: "—", time: "—" };
    try {
      const dateObj = new Date(isoString);
      if (isNaN(dateObj.getTime())) return { date: "—", time: "—" };
      const date = dateObj.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const time = dateObj.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return { date, time };
    } catch {
      return { date: "—", time: "—" };
    }
  };

  const getTypeBadge = (type?: string) => {
    switch (type) {
      case "booking_confirmed":
        return {
          label: "Reserva Confirmada",
          bg: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
          icon: <CheckCircle2 size={14} className="text-emerald-500" />,
        };
      case "booking_cancelled":
        return {
          label: "Reserva Cancelada",
          bg: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30",
          icon: <AlertCircle size={14} className="text-rose-500" />,
        };
      case "dues_pending":
        return {
          label: "Pendência Financeira",
          bg: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
          icon: <AlertCircle size={14} className="text-amber-500" />,
        };
      case "lesson_created":
      case "lesson_reg_created":
      case "lesson_reg_approved":
        return {
          label: "Aula / Turma",
          bg: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30",
          icon: <BookOpen size={14} className="text-sky-500" />,
        };
      case "tournament_request":
        return {
          label: "Torneio",
          bg: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30",
          icon: <Trophy size={14} className="text-purple-500" />,
        };
      default:
        return {
          label: "Notificação do Sistema",
          bg: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30",
          icon: <Bell size={14} className="text-slate-500" />,
        };
    }
  };

  const { date, time } = formatDateTime(notification.createdAt);
  const typeBadge = getTypeBadge(notification.type);

  // Filtra metadados limpos sem expor segredos ou IDs internos confusos
  const cleanMetadataEntries = Object.entries(notification.metadata || {}).filter(
    ([key, value]) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !["token", "password", "secret", "cvv"].some((s) => key.toLowerCase().includes(s))
  );

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="relative w-full max-w-lg bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl shadow-2xl overflow-hidden text-slate-900 dark:text-white flex flex-col max-h-[90vh] my-auto animate-in zoom-in-95 duration-150">
        {/* Cabeçalho do Modal */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-zinc-950/80 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-amber-400/10 dark:bg-amber-400/20 border border-amber-400/30 rounded-xl text-amber-600 dark:text-amber-400">
              <Bell size={18} />
            </div>
            <div>
              <h3 className="font-display font-black text-base text-slate-900 dark:text-white">
                Detalhes da Notificação
              </h3>
              <p className="text-[11px] font-bold text-slate-500 dark:text-zinc-400">
                Informações completas do aviso
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl transition cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Corpo do Detalhe */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Badge de Categoria e Status */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${typeBadge.bg}`}
            >
              {typeBadge.icon}
              <span>{typeBadge.label}</span>
            </span>

            <span
              className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                notification.read
                  ? "bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                  : "bg-amber-400/20 text-amber-700 dark:text-amber-300 border border-amber-400/40"
              }`}
            >
              {notification.read ? (
                <>
                  <ShieldCheck size={12} /> Lida
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500 block animate-pulse" /> Não lida
                </>
              )}
            </span>
          </div>

          {/* Título e Mensagem Principais */}
          <div className="space-y-2 bg-slate-50 dark:bg-zinc-950/50 p-4 rounded-2xl border border-slate-100 dark:border-zinc-800/80">
            <h4 className="text-base font-black text-slate-900 dark:text-white leading-snug">
              {notification.title}
            </h4>
            <p className="text-sm text-slate-700 dark:text-zinc-300 leading-relaxed whitespace-pre-line">
              {notification.message}
            </p>
          </div>

          {/* Data e Hora */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-100/60 dark:bg-zinc-800/40 rounded-xl border border-slate-200/50 dark:border-zinc-800 flex items-center gap-2.5">
              <Calendar size={16} className="text-amber-500 shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 block">
                  Data de emissão
                </span>
                <span className="text-xs font-black text-slate-800 dark:text-zinc-200">
                  {date}
                </span>
              </div>
            </div>

            <div className="p-3 bg-slate-100/60 dark:bg-zinc-800/40 rounded-xl border border-slate-200/50 dark:border-zinc-800 flex items-center gap-2.5">
              <Clock size={16} className="text-amber-500 shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 block">
                  Horário
                </span>
                <span className="text-xs font-black text-slate-800 dark:text-zinc-200">
                  {time}
                </span>
              </div>
            </div>
          </div>

          {/* Metadados adicionais, se houver */}
          {cleanMetadataEntries.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-black text-slate-500 dark:text-zinc-400 flex items-center gap-1.5 uppercase tracking-wider">
                <Tag size={12} /> Detalhes Complementares
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 dark:bg-zinc-950/40 p-3 rounded-2xl border border-slate-200/60 dark:border-zinc-800/60">
                {cleanMetadataEntries.map(([key, val]) => (
                  <div key={key} className="text-xs">
                    <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 block capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}:
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-zinc-200 break-words">
                      {typeof val === "object" ? JSON.stringify(val) : String(val)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Rodapé com Ações */}
        <div className="p-4 bg-slate-50 dark:bg-zinc-950/80 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-end gap-3">
          {notification.linkUrl && (
            <button
              type="button"
              onClick={handleActionClick}
              className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black rounded-xl transition text-xs flex items-center gap-1.5 cursor-pointer shadow-md"
            >
              <span>Acessar conteúdo</span>
              <ExternalLink size={14} />
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 dark:bg-zinc-800 hover:bg-slate-300 dark:hover:bg-zinc-700 text-slate-800 dark:text-white font-bold rounded-xl transition text-xs cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modalContent, document.body) : modalContent;
};
